import { graphql } from '@/__generated__';
import { ExposureFilter } from '@/__generated__/graphql';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { usePaginatedQuery } from '@/reducers/usePaginatedQuery';
import { truthy } from '@/util/array';

export type AppliedExposureListNode = NonNullable<
  ReturnType<typeof useDiscoveryAppliedExposures>['data']
>[number];

export const GetExposures = graphql(/* GraphQL */ `
  query AppliedExposures(
    $first: Int!
    $after: String
    $environmentId: BigInt!
    $userId: BigInt
    $filter: ExposureFilter
  ) {
    environment(id: $environmentId, userId: $userId) {
      applied {
        lastUpdatedAt
        exposures(first: $first, after: $after, filter: $filter) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              uniqueId
              tags
              description
              meta
              exposureType
              maturity
              name
              packageName
              ownerEmail
              ownerName
              manifestGeneratedAt
              autoBiProvider
              freshnessStatus
              quality
              upstreamStats
              maxSnapshottedAt
              healthIssues
            }
          }
        }
      }
    }
  }
`);

type ExposureByEnvironmentParams = {
  first: number;
  after?: string;
  environmentId: number | undefined;
  userId?: number | 'loading' | null;
  filter?: Partial<ExposureFilter>;
  pause?: boolean;
};

export const useDiscoveryAppliedExposures = ({
  first,
  after,
  environmentId,
  userId,
  filter,
  pause,
}: ExposureByEnvironmentParams) => {
  const query = usePaginatedQuery(
    {
      query: GetExposures,
      variables: {
        first,
        after,
        environmentId,
        userId: userId as number | null,
        filter,
      },
      pause:
        pause ||
        !environmentId ||
        filter?.uniqueIds?.length === 0 ||
        userId === 'loading',
    },
    (data) => {
      const exposures = data.environment.applied?.exposures;
      const pageInfo = exposures?.pageInfo;
      if (exposures == null || pageInfo == null) return undefined;
      return {
        totalCount: exposures.totalCount ?? null,
        endCursor: pageInfo.endCursor ?? null,
        hasNextPage: pageInfo.hasNextPage,
      };
    },
    (data) => {
      return data.environment.applied?.exposures.edges
        .map((edge) => edge.node)
        .filter(truthy);
    },
    (data) => {
      return data.environment.applied?.lastUpdatedAt;
    },
  );

  useCacheMonitor({ applied: query.lastUpdatedAt });

  return query;
};
